import { Commit, Dispatch } from 'vuex'
import { isApiError } from '@/types/api'
import {
  cancelFactoryRun as apiCancelFactoryRun,
  createFactory as apiCreateFactory,
  createFactoryRun as apiCreateFactoryRun,
  createFactorySchedule as apiCreateFactorySchedule,
  deleteFactory as apiDeleteFactory,
  deleteFactorySchedule as apiDeleteFactorySchedule,
  getHydratedFactoryStepRun as apiGetHydratedFactoryStepRun,
  getFactoryRun as apiGetFactoryRun,
  listFactories as apiListFactories,
  listFactoryRunsForFactory as apiListFactoryRunsForFactory,
  listFactorySchedules as apiListFactorySchedules,
  listFactoryStepRuns as apiListFactoryStepRuns,
  pauseFactoryRun as apiPauseFactoryRun,
  resumeFactoryRun as apiResumeFactoryRun,
  toggleFactorySchedule as apiToggleFactorySchedule,
  updateFactory as apiUpdateFactory,
  updateFactorySchedule as apiUpdateFactorySchedule
} from '@/utils/api'
import type {
  FactoryConfig,
  FactoryRunRecord,
  FactoryScheduleRecord,
  FactoryStepRunRecord,
  HydratedFactoryStepRunRecord,
  NewFactorySchedulePayload
} from '@/types/factories'

export interface FactoriesState {
  configs: Record<string, FactoryConfig>
  runIdsByFactoryId: Record<string, string[]>
  runsById: Record<string, FactoryRunRecord>
  stepRunIdsByRunId: Record<string, string[]>
  stepRunsById: Record<string, FactoryStepRunRecord>
  hydratedStepRunsById: Record<string, HydratedFactoryStepRunRecord>
  schedulesByFactoryId: Record<string, FactoryScheduleRecord[]>
  loadingRunLists: Record<string, boolean>
  loadingStepRunLists: Record<string, boolean>
}

export const factories = () => ({
  state: <FactoriesState>{
    configs: {},
    runIdsByFactoryId: {},
    runsById: {},
    stepRunIdsByRunId: {},
    stepRunsById: {},
    hydratedStepRunsById: {},
    schedulesByFactoryId: {},
    loadingRunLists: {},
    loadingStepRunLists: {}
  },
  mutations: {
    setFactoryConfigs(state: FactoriesState, configs: FactoryConfig[]) {
      state.configs = configs.reduce((acc, config) => {
        acc[config.id] = config
        return acc
      }, {} as Record<string, FactoryConfig>)
    },
    setFactoryConfig(state: FactoriesState, config: FactoryConfig) {
      state.configs[config.id] = config
    },
    deleteFactoryConfig(state: FactoriesState, factoryId: string) {
      delete state.configs[factoryId]
      delete state.runIdsByFactoryId[factoryId]
      delete state.schedulesByFactoryId[factoryId]
    },
    setFactoryRuns(
      state: FactoriesState,
      { factoryId, runs, append }: { factoryId: string; runs: FactoryRunRecord[]; append?: boolean }
    ) {
      runs.forEach((run) => {
        state.runsById[run.runId] = run
      })
      const existingIds = append ? state.runIdsByFactoryId[factoryId] || [] : []
      const mergedIds = [...existingIds, ...runs.map((run) => run.runId)]
      state.runIdsByFactoryId[factoryId] = [...new Set(mergedIds)].sort((a, b) => {
        const aRun = state.runsById[a]
        const bRun = state.runsById[b]
        return (bRun?.updatedAt || 0) - (aRun?.updatedAt || 0)
      })
    },
    mergeFactoryRun(state: FactoriesState, run: FactoryRunRecord) {
      state.runsById[run.runId] = run
      if (!run.factoryConfigId) return
      const ids = new Set(state.runIdsByFactoryId[run.factoryConfigId] || [])
      ids.add(run.runId)
      state.runIdsByFactoryId[run.factoryConfigId] = [...ids].sort((a, b) => {
        const aRun = state.runsById[a]
        const bRun = state.runsById[b]
        return (bRun?.updatedAt || 0) - (aRun?.updatedAt || 0)
      })
    },
    setFactoryStepRuns(
      state: FactoriesState,
      { runId, stepRuns, append }: { runId: string; stepRuns: FactoryStepRunRecord[]; append?: boolean }
    ) {
      stepRuns.forEach((stepRun) => {
        state.stepRunsById[stepRun.stepRunId] = stepRun
      })
      const existingIds = append ? state.stepRunIdsByRunId[runId] || [] : []
      const mergedIds = [...existingIds, ...stepRuns.map((stepRun) => stepRun.stepRunId)]
      state.stepRunIdsByRunId[runId] = [...new Set(mergedIds)].sort((a, b) => {
        const aRun = state.stepRunsById[a]
        const bRun = state.stepRunsById[b]
        return (aRun?.sequence || 0) - (bRun?.sequence || 0)
      })
    },
    mergeFactoryStepRun(state: FactoriesState, stepRun: FactoryStepRunRecord) {
      state.stepRunsById[stepRun.stepRunId] = stepRun
      const ids = new Set(state.stepRunIdsByRunId[stepRun.runId] || [])
      ids.add(stepRun.stepRunId)
      state.stepRunIdsByRunId[stepRun.runId] = [...ids].sort((a, b) => {
        const aRun = state.stepRunsById[a]
        const bRun = state.stepRunsById[b]
        return (aRun?.sequence || 0) - (bRun?.sequence || 0)
      })
    },
    setHydratedFactoryStepRun(state: FactoriesState, hydrated: HydratedFactoryStepRunRecord) {
      state.hydratedStepRunsById[hydrated.stepRun.stepRunId] = hydrated
      state.stepRunsById[hydrated.stepRun.stepRunId] = hydrated.stepRun
    },
    setFactorySchedules(state: FactoriesState, schedules: FactoryScheduleRecord[]) {
      const grouped: Record<string, FactoryScheduleRecord[]> = {}
      schedules.forEach((schedule) => {
        grouped[schedule.factoryConfigId] = [...(grouped[schedule.factoryConfigId] || []), schedule]
      })
      state.schedulesByFactoryId = grouped
    },
    mergeFactorySchedule(state: FactoriesState, schedule: FactoryScheduleRecord) {
      const current = state.schedulesByFactoryId[schedule.factoryConfigId] || []
      const filtered = current.filter((item) => item.id !== schedule.id)
      state.schedulesByFactoryId[schedule.factoryConfigId] = [...filtered, schedule].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    },
    deleteFactorySchedule(state: FactoriesState, schedule: FactoryScheduleRecord) {
      const current = state.schedulesByFactoryId[schedule.factoryConfigId] || []
      state.schedulesByFactoryId[schedule.factoryConfigId] = current.filter((item) => item.id !== schedule.id)
    },
    setFactoryRunListLoading(state: FactoriesState, { factoryId, value }: { factoryId: string; value: boolean }) {
      state.loadingRunLists[factoryId] = value
    },
    setFactoryStepRunListLoading(state: FactoriesState, { runId, value }: { runId: string; value: boolean }) {
      state.loadingStepRunLists[runId] = value
    }
  },
  actions: {
    async fetchFactories({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await apiListFactories()
      if (isApiError(response) || !response.success) {
        dispatch('addLogEvent', { title: 'Factory Fetch Error', content: response }, { root: true })
        return false
      }
      commit('setFactoryConfigs', response.data)
      return true
    },
    async saveFactory(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: FactoriesState },
      config: FactoryConfig
    ) {
      const exists = Boolean(state.configs[config.id])
      const response = exists ? await apiUpdateFactory(config.id, config) : await apiCreateFactory(config)
      if (isApiError(response) || !response.success) {
        dispatch(
          'updateSnackbar',
          { text: `Factory Save Error: ${isApiError(response) ? response.message : 'Unable to save factory'}`, color: 'error' },
          { root: true }
        )
        dispatch('addLogEvent', { title: 'Factory Save Error', content: response }, { root: true })
        return false
      }
      commit('setFactoryConfig', response.data)
      dispatch('markConfigResourcesDirty', ['factories'])
      return true
    },
    async deleteFactory({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, factoryId: string) {
      const response = await apiDeleteFactory(factoryId)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Factory Delete Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Factory Delete Error', content: response }, { root: true })
        return false
      }
      commit('deleteFactoryConfig', factoryId)
      dispatch('markConfigResourcesDirty', ['factories'])
      return true
    },
    async createFactoryRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, payload: { factoryConfigId: string; initialCarryPayload?: string }) {
      const response = await apiCreateFactoryRun(payload.factoryConfigId, payload.initialCarryPayload)
      if (isApiError(response) || !response.success) {
        dispatch('updateSnackbar', { text: `Factory Run Error: ${isApiError(response) ? response.message : 'Unable to start factory run'}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Factory Run Error', content: response }, { root: true })
        return response
      }
      commit('mergeFactoryRun', response.data)
      return response
    },
    async fetchFactoryRun({ commit }: { commit: Commit }, runId: string) {
      const response = await apiGetFactoryRun(runId)
      if (isApiError(response) || !response.success) {
        return false
      }
      commit('mergeFactoryRun', response.data)
      return true
    },
    async fetchFactoryRunsForFactory(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { factoryId, limit = 20, offset = 0, append = false }: { factoryId: string; limit?: number; offset?: number; append?: boolean }
    ) {
      commit('setFactoryRunListLoading', { factoryId, value: true })
      try {
        const response = await apiListFactoryRunsForFactory(factoryId, limit, offset)
        if (isApiError(response) || !response.success) {
          dispatch('addLogEvent', { title: 'Factory Run Fetch Error', content: response }, { root: true })
          return false
        }
        commit('setFactoryRuns', { factoryId, runs: response.data, append })
        return true
      } finally {
        commit('setFactoryRunListLoading', { factoryId, value: false })
      }
    },
    async fetchFactoryRunSteps(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { runId, limit = 100, offset = 0, append = false }: { runId: string; limit?: number; offset?: number; append?: boolean }
    ) {
      commit('setFactoryStepRunListLoading', { runId, value: true })
      try {
        const response = await apiListFactoryStepRuns(runId, limit, offset)
        if (isApiError(response) || !response.success) {
          dispatch('addLogEvent', { title: 'Factory Step Run Fetch Error', content: response }, { root: true })
          return false
        }
        commit('setFactoryStepRuns', { runId, stepRuns: response.data, append })
        return true
      } finally {
        commit('setFactoryStepRunListLoading', { runId, value: false })
      }
    },
    async fetchHydratedFactoryStepRun({ commit }: { commit: Commit }, payload: { runId: string; stepRunId: string }) {
      const response = await apiGetHydratedFactoryStepRun(payload.runId, payload.stepRunId)
      if (isApiError(response) || !response.success) {
        return false
      }
      commit('setHydratedFactoryStepRun', response.data)
      return true
    },
    async pauseFactoryRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, runId: string) {
      const response = await apiPauseFactoryRun(runId)
      if (isApiError(response) || !response.success) return false
      commit('mergeFactoryRun', response.data)
      if (response.noop && response.message) {
        dispatch('updateSnackbar', { text: response.message, color: 'info' })
        dispatch('addLogEvent', { title: 'Factory Pause', content: response.message })
      }
      return true
    },
    async resumeFactoryRun({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, runId: string) {
      const response = await apiResumeFactoryRun(runId)
      if (isApiError(response) || !response.success) return false
      commit('mergeFactoryRun', response.data)
      if (response.noop && response.message) {
        dispatch('updateSnackbar', { text: response.message, color: 'info' })
        dispatch('addLogEvent', { title: 'Factory Resume', content: response.message })
      }
      return true
    },
    async cancelFactoryRun({ commit }: { commit: Commit }, runId: string) {
      const response = await apiCancelFactoryRun(runId)
      if (isApiError(response)) return response
      const refreshed = await apiGetFactoryRun(runId)
      if (!isApiError(refreshed) && refreshed.success) {
        commit('mergeFactoryRun', refreshed.data)
      }
      return response
    },
    async fetchFactorySchedules({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await apiListFactorySchedules()
      if (isApiError(response) || !response.success) {
        dispatch('addLogEvent', { title: 'Factory Schedule Fetch Error', content: response }, { root: true })
        return false
      }
      commit('setFactorySchedules', response.data)
      return true
    },
    async saveFactorySchedule({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, payload: NewFactorySchedulePayload | FactoryScheduleRecord) {
      const response = 'id' in payload
        ? await apiUpdateFactorySchedule(payload.id, payload)
        : await apiCreateFactorySchedule(payload)
      if (isApiError(response) || !response.success) {
        dispatch('updateSnackbar', { text: `Factory Schedule Save Error: ${isApiError(response) ? response.message : 'Unable to save schedule'}`, color: 'error' }, { root: true })
        return false
      }
      commit('mergeFactorySchedule', response.data)
      return true
    },
    async deleteFactorySchedule({ commit, state }: { commit: Commit; state: FactoriesState }, scheduleId: string) {
      const existing = Object.values(state.schedulesByFactoryId).flat().find((schedule) => schedule.id === scheduleId)
      if (!existing) return false
      const response = await apiDeleteFactorySchedule(scheduleId)
      if (isApiError(response) || !response.success) return false
      commit('deleteFactorySchedule', existing)
      return true
    },
    async toggleFactorySchedule({ commit }: { commit: Commit }, scheduleId: string) {
      const response = await apiToggleFactorySchedule(scheduleId)
      if (isApiError(response) || !response.success) return false
      commit('mergeFactorySchedule', response.data)
      return true
    }
  },
  getters: {
    factoryList: (state: FactoriesState): FactoryConfig[] =>
      Object.values(state.configs).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    getFactoryById: (state: FactoriesState) => (id: string): FactoryConfig | undefined => state.configs[id],
    factoryRunsByFactory: (state: FactoriesState) => (factoryId: string): FactoryRunRecord[] =>
      (state.runIdsByFactoryId[factoryId] || []).map((runId) => state.runsById[runId]).filter(Boolean),
    factoryStepRunsByRun: (state: FactoriesState) => (runId: string): FactoryStepRunRecord[] =>
      (state.stepRunIdsByRunId[runId] || []).map((stepRunId) => state.stepRunsById[stepRunId]).filter(Boolean),
    getHydratedFactoryStepRun: (state: FactoriesState) => (stepRunId: string): HydratedFactoryStepRunRecord | undefined =>
      state.hydratedStepRunsById[stepRunId],
    factorySchedulesByFactory: (state: FactoriesState) => (factoryId: string): FactoryScheduleRecord[] =>
      (state.schedulesByFactoryId[factoryId] || []).slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    isFactoryRunListLoading: (state: FactoriesState) => (factoryId: string): boolean => Boolean(state.loadingRunLists[factoryId]),
    isFactoryStepRunListLoading: (state: FactoriesState) => (runId: string): boolean => Boolean(state.loadingStepRunLists[runId])
  }
})
